import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Chip,
  IconButton,
  Autocomplete,
  Checkbox
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Details as DetailsIcon,
  FirstPage as FirstPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage as LastPageIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import moment from 'moment';
import { useGetFilteredLogsQuery } from '../../features/logs/logsApiSlice';
import LogDetailModal from './LogDetailModal';
import { TableContStyle, TableHeadStyle } from '../../Styles/TableStyles';
import { HeadingWrap, Span } from '../../Styles/CommonStyles';
import FilteredLogsFetcher from './FilteredLogsFetcher';
import ExcelDownload from '../../data/exceldownload';
import styled from 'styled-components';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Button2Div = styled.div`
  display: flex;
  justify-content: end;
`;

const Wrapper = styled.div`
  display: flex;
  background-color: white;
  align-items: center;
`;

// Responsive font size function
const getTableFontSize = (breakpoint) => ({
  xs: 11,
  sm: 12,
  md: 13,
  lg: 14
});

// Common ellipsis style for table cells
const ellipsisStyle = {
  fontSize: getTableFontSize(),
  padding: { xs: '8px 4px', sm: '16px 8px' },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Table pagination actions component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Debounce function
function debounce(func, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
}

const SystemLogs = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [tabledata, setTabledata] = useState([]);
  const [allData, setAllData] = useState([]);
  const [downloadData, setDownloadData] = useState([]);

  const [filters, setFilters] = useState({
    action: '',
    collectionName: '',
    userEmail: '',
    ipAddress: '',
    device: '',
    browser: '',
    os: '',
    status: [],
    level: [],
    message: '',
    from_timestamp: '',
    to_timestamp: ''
  });

  // Query for paginated logs
  const { 
    data: logsData, 
    error: logsError, 
    isLoading: logsLoading,
    refetch
  } = useGetFilteredLogsQuery({
    page: page + 1,
    limit: rowsPerPage,
    filters
  }, {
    refetchOnMountOrArgChange: true, // Refetch whenever component mounts or args change
    refetchOnFocus: false // Set to true if you want to refetch when tab gains focus
  });

  // Constants for dropdown options
  const statusOptions = ['success', 'error'];

  // Update table data when API data changes
  useEffect(() => {
    if (logsData?.logs) {
      setTabledata(logsData.logs);
    }
  }, [logsData]);

  // Prepare download data when all data is fetched
  useEffect(() => {
    if (allData?.logs) {
      const tempDownloadData = structuredClone(allData?.logs || []);
      const finalDownloadData = tempDownloadData.map(({ _id, id, ...others }) => ({
        Timestamp: moment(others?.timestamp).format("DD/MM/YYYY HH:mm:ss"),
        Action: others?.action || 'N/A',
        Collection: others?.collectionName || 'N/A',
        Email: others?.userEmail || 'N/A',
        'User ID': others?.userId || 'N/A',
        'User Office': typeof others?.userOffice === 'object' 
          ? JSON.stringify(others?.userOffice) 
          : (others?.userOffice || 'N/A'),
        'User Designation': typeof others?.userDesignation === 'object'
          ? JSON.stringify(others?.userDesignation)
          : (others?.userDesignation || 'N/A'),
        'User Role': typeof others?.userRole === 'object'
          ? JSON.stringify(others?.userRole)
          : (others?.userRole || 'N/A'),
        'IP Address': others?.ipAddress || 'N/A',
        Browser: others?.browser || 'N/A',
        OS: others?.os || 'N/A',
        Device: others?.device || 'N/A',
        Status: others?.status || 'N/A',
        Level: others?.level || 'N/A',
        Message: others?.message || 'N/A',
        'Document ID': others?.documentId || 'N/A'
      }));

      setDownloadData(finalDownloadData);
    }
  }, [allData]);

  // Event handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = debounce((event, newValue, name) => {
    let value;

    if (name) {
      // If a date is selected, check if it's valid and convert to ISO format
      if (newValue?.$d) {
        value = newValue?.toDate().toISOString();
      } else {
        value = newValue;
      }
    } else {
      // Handle non-date input fields
      name = event.target.name;
      value = event.target.value;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    setPage(0); // Reset to first page on filter change
  }, 500);

  const handleDetailsClick = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedLog(null);
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  const safeRender = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  if (logsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="error">
          Error Loading Logs
        </Typography>
        <Typography variant="body1">
          {logsError?.message || logsError?.data?.message || 'Unknown error occurred'}
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <FilteredLogsFetcher filters={filters} onDataFetched={setAllData} />
        
        {/* Header */}
        <HeadingWrap>
          <Span>System Activity Logs</Span>
        </HeadingWrap>

        {/* Excel Download Button */}
        {(allData?.logs && downloadData?.length > 0) && (
          <Button2Div>
            <ExcelDownload data={downloadData} filename={"systemLogs"} sheetName={"Logs"} />
          </Button2Div>
        )}

        {/* Logs Table */}
        <div style={{ 
          width: '100%', 
          padding: '0px',
          boxSizing: 'border-box'
        }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              backgroundColor: TableContStyle.backgroundColor,
              width: '100%',
              margin: { xs: '8px 0', sm: '16px 0' },
              boxShadow: { xs: 1, sm: 3 }
            }}
          >
            <Table 
              sx={{ 
                width: '100%',
                tableLayout: 'fixed'
              }} 
              aria-label="logs table"
            >
              <TableHead style={{ 
                backgroundColor: TableHeadStyle.backgroundColor,
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}>
                <TableRow>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "5%",
                    maxWidth: "5%"
                  }}>
                    S.No.
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "12%",
                    maxWidth: "12%"
                  }} align="left">
                    Time Stamp
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "10%",
                    maxWidth: "10%"
                  }} align="left">
                    Action
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "10%",
                    maxWidth: "10%"
                  }} align="left">
                    Collection
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "15%",
                    maxWidth: "15%"
                  }} align="left">
                    Email
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "10%",
                    maxWidth: "10%"
                  }} align="left">
                    IP Address
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "10%",
                    maxWidth: "10%"
                  }} align="left">
                    Device
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "8%",
                    maxWidth: "8%"
                  }} align="left">
                    Status
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "15%",
                    maxWidth: "15%"
                  }} align="left">
                    Message
                  </TableCell>
                  <TableCell style={TableHeadStyle} sx={{ 
                    width: "5%",
                    maxWidth: "5%"
                  }} align="left">
                    Detail
                  </TableCell>
                </TableRow>

                {/* Search/Filter Row */}
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker", "DatePicker"]} sx={{ margin: "0px", display: "flex" }}>
                        <Wrapper>
                <DatePicker
                            label="From"
                            maxDate={filters?.to_timestamp ? dayjs(filters.to_timestamp) : null}
                            slotProps={{ textField: { size: "small" } }}
                            sx={{ backgroundColor: "white" }}
                            value={filters?.from_timestamp ? dayjs(filters.from_timestamp) : null}
                            format="DD/MM/YYYY"
                            onChange={(newValue) => handleSearchChange(null, newValue, "from_timestamp")}
                          />
                          {filters?.from_timestamp && (
                            <ClearIcon onClick={() => handleSearchChange(null, null, "from_timestamp")} />
                          )}
                        </Wrapper>
                      </DemoContainer>
                    </LocalizationProvider>
                    {filters?.from_timestamp && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DatePicker", "DatePicker"]}>
                          <Wrapper>
                <DatePicker
                              label="To"
                              minDate={filters?.from_timestamp ? dayjs(filters.from_timestamp) : null}
                              slotProps={{ textField: { size: "small" } }}
                              sx={{ backgroundColor: "white" }}
                              value={filters?.to_timestamp ? dayjs(filters.to_timestamp) : null}
                              format="DD/MM/YYYY"
                              onChange={(newValue) => handleSearchChange(null, newValue, "to_timestamp")}
                            />
                            {filters?.to_timestamp && (
                              <ClearIcon onClick={() => handleSearchChange(null, null, "to_timestamp")} />
                            )}
                          </Wrapper>
                        </DemoContainer>
                      </LocalizationProvider>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ 
                        background: "white",
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '8px', sm: '10px' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                      name="action"
                      onChange={handleSearchChange}
                      label="Search Action"
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ 
                        background: "white",
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '8px', sm: '10px' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                      name="collectionName"
                      onChange={handleSearchChange}
                      label="Search Collection"
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ 
                        background: "white",
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '8px', sm: '10px' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                      name="userEmail"
                      onChange={handleSearchChange}
                      label="Search Email"
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                <TextField
                      sx={{ 
                        background: "white",
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '8px', sm: '10px' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                      name="ipAddress"
                      onChange={handleSearchChange}
                      label="Search IP"
                  size="small"
                      variant="outlined"
                  fullWidth
                />
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ 
                        background: "white",
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '8px', sm: '10px' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                      name="device"
                      onChange={handleSearchChange}
                      label="Search Device"
                      size="small"
                  variant="outlined"
                  fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      id="status-search"
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "status")}
                      size="small"
                      value={filters?.status || []}
                      options={statusOptions}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      ListboxProps={{
                        style: {
                          maxHeight: '200px',
                          fontSize: '12px'
                        }
                      }}
                      componentsProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'flip',
                              enabled: false,
                            },
                            {
                              name: 'preventOverflow',
                              enabled: true,
                              options: {
                                altAxis: true,
                                altBoundary: true,
                                tether: true,
                                rootBoundary: 'document',
                              },
                            },
                          ],
                        },
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ 
                          fontSize: "12px",
                          padding: "4px 8px",
                          lineHeight: "1.2",
                          minHeight: "32px",
                          display: "flex",
                          alignItems: "center"
                        }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ 
                              marginRight: "4px", 
                              marginLeft: "0",
                              padding: "2px"
                            }}
                            checked={selected}
                            size="small"
                          />
                          <span style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1
                          }} title={option}>
                            {option}
                          </span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Search Status" 
                          sx={{ 
                            background: "white", 
                            width: "100%",
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '11px', sm: '12px' },
                              padding: { xs: '6px', sm: '8px' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '11px', sm: '12px' }
                            },
                            '& .MuiChip-root': {
                              fontSize: '10px',
                              height: '20px'
                            }
                          }} 
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ 
                        background: "white",
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' },
                          padding: { xs: '8px', sm: '10px' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                      name="message"
                      onChange={handleSearchChange}
                      label="Search Message"
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : tabledata?.length > 0 ? (
                  tabledata.map((log, index) => (
                    <TableRow key={log._id || index}>
                      <TableCell component="th" scope="row" align="center" sx={{
                        ...ellipsisStyle,
                        textAlign: 'center'
                      }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={ellipsisStyle}
                        title={formatTimestamp(log.timestamp)}
                      >
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={ellipsisStyle}
                        title={safeRender(log.action)}
                      >
                        {safeRender(log.action)}
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={ellipsisStyle}
                        title={safeRender(log.collectionName)}
                      >
                        {safeRender(log.collectionName)}
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={ellipsisStyle}
                        title={safeRender(log.userEmail)}
                      >
                        {safeRender(log.userEmail)}
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={{...ellipsisStyle, fontFamily: 'monospace'}}
                        title={safeRender(log.ipAddress)}
                      >
                        {safeRender(log.ipAddress)}
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={ellipsisStyle}
                        title={safeRender(log.device)}
                      >
                        {safeRender(log.device)}
                      </TableCell>
                      <TableCell align="left" sx={ellipsisStyle}>
                        <Chip
                          label={safeRender(log.status)}
                          color={getStatusColor(log.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell 
                        align="left" 
                        sx={ellipsisStyle}
                        title={safeRender(log.message)}
                      >
                        {safeRender(log.message)}
                      </TableCell>
                      <TableCell align="center" sx={{
                        ...ellipsisStyle,
                        textAlign: 'center'
                      }}>
                        <DetailsIcon
                          sx={{ 
                            color: "green", 
                            fontSize: { xs: "20px", sm: "24px", md: "28px" }, 
                            "&:hover": { cursor: "pointer" } 
                          }}
                          onClick={() => handleDetailsClick(log)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No logs found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                    colSpan={10}
                    count={logsData?.totalFilteredLogs || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { "aria-label": "rows per page" },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        fontSize: { xs: '12px', sm: '14px' },
                        minHeight: { xs: '40px', sm: '52px' },
                        paddingLeft: 0
                      },
                      '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: { xs: '12px', sm: '14px' }
                      },
                      '.MuiTablePagination-spacer': {
                        display: 'none'
                      }
                    }}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>

        {/* Detail Modal */}
        <LogDetailModal
          open={detailModalOpen}
          onClose={handleDetailModalClose}
          logData={selectedLog}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SystemLogs;
